import React, { useEffect, useMemo, useState } from "react";
import { createGame, selectCell, getAIMove, applyAIMove } from "./engine.js";
import type { CheckersGame } from "./types.js";
import { EMPTY, RED, BLACK, RED_KING, BLACK_KING } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

type Mode = "cpu" | "local";

export default function CheckersPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [mode, setMode] = useState<Mode>("cpu");
  const [p1Name, setP1Name] = useState("Player 1");
  const [p2Name, setP2Name] = useState("Player 2");
  const [game, setGame] = useState<CheckersGame>(() => createGame());
  const [gameId, setGameId] = useState(0);

  const p1 = p1Name.trim() || "Player 1";
  const p2 = mode === "cpu" ? "Computer" : p2Name.trim() || "Player 2";
  const isCpuTurn = mode === "cpu" && game.turn === 2 && game.winner === 0 && !game.isDraw;

  const status = useMemo(() => {
    if (game.winner === 1) return `${p1} wins!`;
    if (game.winner === 2) return `${p2} wins!`;
    if (game.isDraw) return "Draw game.";
    return `${game.turn === 1 ? p1 : p2}'s turn`;
  }, [game, p1, p2]);

  function handleCellClick(r: number, c: number) {
    if (game.winner !== 0 || game.isDraw) return;
    if (isCpuTurn) return;
    setGame((prev) => selectCell(prev, r, c));
  }

  function resetGame() {
    setGame(createGame());
    setGameId((n) => n + 1);
  }

  useEffect(() => {
    if (!isCpuTurn) return;
    const timer = window.setTimeout(() => {
      setGame((prev) => {
        const move = getAIMove(prev);
        if (!move) return prev;
        return applyAIMove(prev, move.sr, move.sc, move.dr, move.dc);
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [isCpuTurn, game.turn, game.multiJump, gameId]);

  function getPieceChar(p: number): string {
    if (p === RED) return "O";
    if (p === BLACK) return "O";
    if (p === RED_KING) return "♛";
    if (p === BLACK_KING) return "♛";
    return "";
  }

  function getPieceColor(p: number): string {
    if (p === RED || p === RED_KING) return "red";
    if (p === BLACK || p === BLACK_KING) return "black";
    return "";
  }

  function isSelected(r: number, c: number): boolean {
    return game.selected?.r === r && game.selected?.c === c;
  }

  function isValid(r: number, c: number): boolean {
    return game.validMoves.some((m) => m.r === r && m.c === c);
  }

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.winner !== 0} winnerName={game.winner === 1 ? p1 : game.winner === 2 ? p2 : undefined} />
      {!hideTitle && <h1 className="title">Checkers</h1>}

      <section className="control-panel">
        <div className="row two-col">
          <label>Mode <select value={mode} onChange={(e) => { setMode(e.target.value as Mode); setGame(createGame()); }}>
            <option value="cpu">Single Player vs Computer</option>
            <option value="local">Two Players Local</option>
          </select></label>
        </div>
        <div className="row two-col">
          <label>Player 1 <input value={p1Name} onChange={(e) => setP1Name(e.target.value)} maxLength={20} /></label>
          <label>{mode === "cpu" ? "Computer" : "Player 2"} <input value={mode === "cpu" ? "Computer" : p2Name} onChange={(e) => setP2Name(e.target.value)} maxLength={20} disabled={mode === "cpu"} /></label>
        </div>
        <div className="actions">
          <button onClick={resetGame}>New Game</button>
        </div>
      </section>

      <section className="board-panel">
        <p className="status">{status}</p>
        <div key={gameId} className="checkers-board">
          {game.board.map((row, r) =>
            row.map((cell, c) => {
              const black = (r + c) % 2 === 1;
              const showValid = black && isValid(r, c);
              return (
                <div
                  key={`${r}-${c}`}
                  className={`checkers-cell ${black ? "dark" : "light"} ${isSelected(r, c) ? "selected" : ""} ${showValid ? "valid" : ""}`}
                  onClick={() => handleCellClick(r, c)}
                >
                  {cell !== EMPTY && (
                    <div className={`checkers-piece ${getPieceColor(cell)} ${cell === RED_KING || cell === BLACK_KING ? "king" : ""}`}>
                      {getPieceChar(cell)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. Click a piece to select it, then click a highlighted dark square to move.</p>
          <p>2. Jump over opponent pieces to capture them. Jumps are mandatory!</p>
          <p>3. Reach the opposite end to become a King. Capture all pieces to win.</p>
        </div>
      </section>
    </div>
  );
}
